import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Motorcycle, SignIn, Phone, LockKey, ArrowRight,
} from "@phosphor-icons/react";
import { gigApi, gigFormatErr, GIG_TOKEN_KEY, GIG_PARTNER_KEY } from "../../lib/gigApi";

export default function PartnerAppLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = phone, 2 = otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in?
  if (localStorage.getItem(GIG_TOKEN_KEY)) {
    return <Navigate to="/partner-app" replace />;
  }

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6,15}$/.test(phone.trim())) {
      toast.error("Enter a valid phone");
      return;
    }
    setBusy(true);
    try {
      const r = await gigApi.post("/gig-partner-app/login/start", { phone: phone.trim() });
      setDevOtp(r.data.dev_otp || "");
      toast.success(r.data.dev_otp ? `Demo OTP: ${r.data.dev_otp}` : "OTP sent");
      setStep(2);
    } catch (e2) { toast.error(gigFormatErr(e2)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) { toast.error("Enter OTP"); return; }
    setBusy(true);
    try {
      const r = await gigApi.post("/gig-partner-app/login/verify",
                                     { phone: phone.trim(), otp: otp.trim() });
      localStorage.setItem(GIG_TOKEN_KEY, r.data.access_token);
      localStorage.setItem(GIG_PARTNER_KEY, JSON.stringify(r.data.partner));
      nav("/partner-app", { replace: true });
    } catch (e2) { toast.error(gigFormatErr(e2)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0F1F0C] text-white flex items-center justify-center p-6"
          data-testid="partner-app-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#C7E0C2] flex items-center justify-center">
            <Motorcycle size={32} className="text-[#0F1F0C]" weight="duotone"/>
          </div>
          <h1 className="text-3xl font-heading mt-4">Partner app</h1>
          <p className="text-sm text-white/60 mt-1">
            Accept nearby jobs. Earn on your terms.
          </p>
        </div>
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
          {step === 1 ? (
            <form onSubmit={sendOtp} className="space-y-4">
              <label className="block text-[11px] uppercase tracking-wider text-white/60">
                Phone number
              </label>
              <div className="flex items-center gap-2 bg-white/10 rounded-md px-3 h-11">
                <Phone size={16} className="text-white/60"/>
                <input value={phone} onChange={e => setPhone(e.target.value)}
                        placeholder="10-digit mobile" inputMode="numeric"
                        data-testid="partner-app-phone"
                        className="flex-1 bg-transparent outline-none text-sm"/>
              </div>
              <button type="submit" disabled={busy} data-testid="partner-app-send-otp"
                       className="h-11 w-full rounded-md bg-[#C7E0C2] text-[#0F1F0C] text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? "Sending…" : "Send OTP"} <ArrowRight size={14}/>
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <label className="block text-[11px] uppercase tracking-wider text-white/60">
                Enter the OTP sent to {phone}
              </label>
              <div className="flex items-center gap-2 bg-white/10 rounded-md px-3 h-11">
                <LockKey size={16} className="text-white/60"/>
                <input value={otp} onChange={e => setOtp(e.target.value)}
                        placeholder="6-digit OTP" inputMode="numeric"
                        data-testid="partner-app-otp"
                        className="flex-1 bg-transparent outline-none text-sm tracking-[0.5em]"/>
              </div>
              {devOtp && <div className="text-[11px] text-white/40">Demo code: {devOtp}</div>}
              <button type="submit" disabled={busy} data-testid="partner-app-verify"
                       className="h-11 w-full rounded-md bg-[#C7E0C2] text-[#0F1F0C] text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? "Verifying…" : "Verify & sign in"} <SignIn size={14}/>
              </button>
              <button type="button" onClick={() => setStep(1)}
                       data-testid="partner-app-back"
                       className="h-9 w-full text-xs text-white/60">
                ← use a different number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
